/**
 * @file SelfSubmissionPlayerSuggestionInput component
 *
 * Will show suggested result as you type
 */

import { debounce } from 'lodash-es';
import Request from '../../../utils/request';
import Data from '../../../utils/dom/data';
import EventHandler from '../../../utils/dom/event-handler';
import Manipulator from '../../../utils/dom/manipulator';
import BaseComponent from '../../BaseComponent';

const NAME = 'SelfSubmissionPlayerSuggestionInput';

const DATA_KEY = 'lta.selfSubmissionPlayerSuggestionInput';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;

const SELECTOR_DATA_TOGGLE = '[data-lta-selfSubmissionPlayerSuggestionInput]';

export default class SelfSubmissionPlayerSuggestionInput extends BaseComponent {
	constructor(element) {
		super(element);

		this.state = {
			typing: false,
			inputStringLength: 3,
			inputNumericLength: 6,
			selectedResults: {}
		};
		this._dataFetchInProgress = false;

		this.suggestResults();
	}

	static get NAME() {
		return NAME;
	}

	// Getters
	static get DATA_KEY() {
		return DATA_KEY;
	}

	dispose() {
		super.dispose();
		// Remove event handlers, reset constructor public settings etc.
	}

	suggestResults() {
		if (this._element !== null) {
			const settings = Manipulator.getDataAttributes(this._element);

			const closeButton = this._element.querySelector('.js-lta-text-ss-player-input--close');
			const resultWrapper = this._element.querySelector('.lta-results-ss-player-wrapper');
			const resultList = this._element.querySelector('.lta-suggest-ss-player-results ul');
			const itemClass = 'lta-suggest-ss-player-results--item';
			const input = closeButton.previousElementSibling;
			const inputWrapper = this._element.querySelector('#autosuggest-ss-player-input');

			input.addEventListener('input', (event) => {
				const isNotANumber = Number.isNaN(Number(event.target.value));
				if (isNotANumber) {
					this.state.typing =
						event.target.value.trim() !== '' && event.target.value.length >= this.state.inputStringLength;
				} else {
					this.state.typing =
						event.target.value.trim() !== '' && event.target.value.length >= this.state.inputNumericLength;
				}

				if (this.state.typing) {
					closeButton.classList.remove('hide-btn');
					resultWrapper.classList.add('show-results');
				} else {
					closeButton.classList.add('hide-btn');
					resultWrapper.classList.remove('show-results');
				}
			});

			const createItemMarkup = (item, index) => {
				const { firstname, lastname, gender, ltanumber, city, county } = item;
				const listElement = document.createElement('li');
				const button = document.createElement('button');
				const br = document.createElement('br');
				const firstLine = document.createTextNode(`${firstname} ${lastname} (${gender}) (${ltanumber})`);
				const secondLine = document.createTextNode(`${city}, ${county}`);

				button.setAttribute('class', `${itemClass} u-pl-3 u-pt-1 u-pb-1`);
				button.setAttribute('type', 'button');
				button.setAttribute('role', 'option');
				button.setAttribute('id', `suggestion-${index + 1}`);
				button.dataset.ltanumber = ltanumber;
				button.append(firstLine);
				button.append(br);
				button.append(secondLine);
				listElement.append(button);
				return listElement;
			};

			const dataRequest = async () => {
				if (Number.isNaN(input.value)) {
					if (input.value.length < this.state.inputStringLength) {
						this._dataFetchInProgress = false;
						return;
					}
				} else if (!Number.isNaN(input.value) && input.value.length < this.state.inputStringLength) {
					this._dataFetchInProgress = false;
					return;
				}
				// execute only if characters length >= of specific number

				if (this._element.dataset.endpoint) {
					const { affiliatedId } = Manipulator.getDataAttributes(inputWrapper);

					let affiliateValue;
					if (document.querySelector(`#${affiliatedId}`)) {
						affiliateValue = document.querySelector(`#${affiliatedId}`).checked;
					}
					const { endpoint, endpointkey } = this._element.dataset;
					// data-ss-primary-contact-id is mandatory
					const data = await Request.get(endpoint, {
						[endpointkey]: input.value.trim(),
						onlyAffiliations: !!affiliateValue,
						playerContactId: document
							.querySelector('#self-submission-data-attr')
							.getAttribute('data-ss-primary-contact-id')
					});
					const { playerDetails } = data;

					resultList.innerHTML = '';
					const filteredPlayers =
						settings.filterPrevious && playerDetails.length > 0
							? playerDetails.filter((player) => this.state.selectedResults[player.id] === undefined)
							: playerDetails;

					if (filteredPlayers.length > 0) {
						resultWrapper.classList.remove('has-no-results');
					} else {
						resultWrapper.classList.add('has-no-results');
					}

					for (const [index, player] of filteredPlayers.entries()) {
						resultList.append(createItemMarkup(player, index));
					}
					inputWrapper.setAttribute('aria-expanded', 'true');
					const itemButtons = resultList.querySelectorAll(`.${itemClass}`);
					if (itemButtons.length > 0) {
						for (const button of itemButtons)
							button.addEventListener('click', (event) => {
								const { ltanumber } = event.currentTarget.dataset;
								const playerData = playerDetails.find((player) => player.ltanumber === ltanumber);
								const customEvent = new CustomEvent('playerSelect', { bubbles: true, detail: playerData });
								this.state.selectedResults[ltanumber] = playerData;
								this._element.dispatchEvent(customEvent);
								resultWrapper.classList.remove('show-results');
							});
					}

					this._dataFetchInProgress = false;
				}
			};

			input.addEventListener('keydown', () => {
				resultList.textContent = '';

				debounce(() => {
					if (!this._dataFetchInProgress) {
						this._dataFetchInProgress = true;
						dataRequest();
					}
				}, 800)();
			});

			closeButton.addEventListener('click', () => {
				input.value = '';
				resultList.textContent = '';
				closeButton.classList.add('hide-btn');
				resultWrapper.classList.remove('show-results');
				inputWrapper.setAttribute('aria-expanded', 'false');
				input.focus();
				this.state.typing = false;
			});

			document.addEventListener('click', (event) => {
				const isClickInside = this._element.contains(event.target);
				if (!isClickInside) {
					resultWrapper.classList.remove('show-results');
					inputWrapper.setAttribute('aria-expanded', 'false');
				}
			});
		}
	}

	clearSelectedResults() {
		this.state.selectedResults = {};
	}

	clearSelectedResult(id) {
		delete this.state.selectedResults[id];
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	// eslint-disable-next-line no-invalid-this
	Data.getData(this, DATA_KEY) || new SelfSubmissionPlayerSuggestionInput(this);
});
